import { usePermissions } from "../context/PermissionContext";
import { accessAreaForScreen } from "../config/userAccessAreas";

/**
 * @param {string} screenKey
 * @param {{ bypassAdmin?: boolean }} [options] Set bypassAdmin:false for
 *   actions the backend deliberately withholds from Super Admin even though
 *   they can view everything — e.g. elog.deck/elog.engine "add": vessel
 *   operational logs are crew-authored, and admin is granted permissions,
 *   not a report author. Without this, the UI would show a control (like
 *   "New Report") that the real API call behind it then rejects.
 */
export const useCan = (screenKey, options = {}) => {
  const { bypassAdmin = true } = options;
  const { permissions, roleName, userRestricted, accessAreas, loading } = usePermissions();
  const roleKey = roleName ? `${roleName.toLowerCase()}.${screenKey}` : null;
  const merged = permissions?.[roleKey] || permissions?.[screenKey] || {};
  const screenArea = accessAreaForScreen(screenKey);
  const insideUserBoundary =
    !userRestricted ||
    screenArea === "account-shell" ||
    (screenArea && accessAreas.includes(screenArea));
  const isAdmin =
    bypassAdmin &&
    insideUserBoundary &&
    !!permissions?.["superadmin.dashboard"]?.view;

  // Admin / Super Admin: full UI access across modules (API may still
  // block shore-admin writes on vessel operational logs).
  if (isAdmin) {
    return {
      canView: true,
      canAdd: true,
      canEdit: true,
      canDelete: true,
      canProcess: true,
      loading,
      isAdmin: true,
    };
  }

  return {
    canView: !!merged.view,
    canAdd: !!merged.add,
    canEdit: !!merged.edit,
    canDelete: !!merged.delete,
    canProcess: !!merged.process,
    loading,
    isAdmin: false,
  };
};
